import { useState, useEffect } from 'react';
import { FiChevronRight, FiPlay } from 'react-icons/fi';
import { BsHeartPulse } from 'react-icons/bs';
import ContactModal from '../ContactModal';

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const slides = [
    {
      id: 1,
      subtitle: "IT & Software Design",
      subtitleIcon: <BsHeartPulse className="w-4 h-4 mr-2" />,
      titleLine1: "Digital Market &",
      titleLine2: "Graphic Design",
      image: "/vr-banner.png",
      year: "2026"
    },
    {
      id: 2,
      subtitle: "Welcome To Sutrasync",
      subtitleIcon: null,
      titleLine1: "IT Solutions &",
      titleLine2: "Software Development",
      image: "/marketer-banner.png",
      year: "2026"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000); // 6 seconds per slide
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const TechCircle = () => (
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] sm:w-[600px] sm:h-[600px] lg:w-[700px] lg:h-[700px] -z-10 pointer-events-none flex items-center justify-center">
      {/* Outer Dashed Ring */}
      <div className="absolute inset-0 border-[2px] border-dashed border-gray-400 rounded-full animate-spin-slow"></div>
      
      {/* Middle Solid Ring with Opacity */}
      <div className="absolute inset-[10%] border-[1px] border-gray-300 rounded-full"></div>
      
      {/* Inner Rotating Dashed Ring (Reverse) */}
      <div className="absolute inset-[18%] border-[2px] border-dashed border-gray-500 rounded-full animate-spin-reverse-slower"></div>
      
      {/* Decorative Dots on a Ring */}
      <div className="absolute inset-0 animate-spin-slower">
        <div className="absolute top-0 left-1/2 w-3 h-3 bg-[#22446b] rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-1/2 w-3 h-3 bg-[#22446b] rounded-full transform -translate-x-1/2 translate-y-1/2"></div>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen overflow-hidden font-sans bg-gradient-to-br from-white to-[#e8e8e8]">
      {/* Background Shapes & Particles (Fixed across slides) */}
      <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute -top-40 -right-40 w-[800px] h-[800px] bg-gray-200/40 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute top-[20%] left-[10%] w-32 h-32 border-[15px] border-[#e0e0e0] rounded-full animate-float"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-gray-100 rounded-full blur-xl animate-bounce-slow"></div>
          
          {/* Dotted Pattern */}
          <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-20" 
               style={{backgroundImage: 'radial-gradient(#000000 0.5px, transparent 0.5px)', backgroundSize: '24px 24px'}}></div>
      </div>

      {/* Main Slider Container */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out transform ${
            index === currentSlide ? 'opacity-100 translate-x-0 z-10' : 'opacity-0 translate-x-12 z-0'
          }`}
        >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full pt-20 sm:pt-32 lg:pt-64 pb-8 sm:pb-12 lg:pb-20 flex flex-col lg:flex-row items-center justify-between relative">
            
            {/* Background Large Year Text */}
            <div className={`absolute top-[15%] left-[-5%] lg:left-[-2%] text-[200px] sm:text-[300px] lg:text-[400px] font-bold text-[#f0f0f0] leading-none select-none -z-10 transition-all duration-1000 delay-300 font-rajdhani opacity-60 ${
                index === currentSlide ? 'translate-y-0' : 'translate-y-10'
            }`}>
              {slide.year}
            </div>

            {/* Left Content (Text) */}
            <div className="w-full lg:w-1/2 z-20">
              <div className="space-y-6 lg:pl-4">
                 {/* Subtitle */}
                 <div className={`inline-flex items-center text-[#106bcb] font-semibold tracking-wide text-lg py-2 rounded-lg transition-all duration-700 delay-300 ${
                     index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                 }`}>
                   {slide.subtitleIcon}
                   <span className="uppercase font-rajdhani">{slide.subtitle}</span>
                 </div>

                 {/* Main Title */}
                 <h1 className="text-5xl sm:text-6xl lg:text-[85px] font-bold text-[#22446b] leading-[1.05] font-rajdhani tracking-tight">
                   <span className={`block transition-all duration-700 delay-500 ${
                       index === currentSlide ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                   }`}>
                     {slide.titleLine1}
                   </span>
                   <span className={`block text-transparent bg-clip-text bg-gradient-to-r from-[#22446b] to-gray-700 transition-all duration-700 delay-700 ${
                       index === currentSlide ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                   }`}>
                     {slide.titleLine2}
                   </span>
                 </h1>

                 {/* Buttons */}
                 <div className={`flex flex-wrap gap-5 pt-8 transition-all duration-700 delay-1000 ${
                     index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                 }`}>
                   <button 
                     onClick={() => setIsModalOpen(true)}
                     className="bg-gradient-to-r from-[#22446b] to-[#338edc] text-white px-10 py-4 rounded-lg font-bold font-rajdhani text-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center gap-2 group"
                   >
                     Get Started
                     <FiChevronRight className="group-hover:translate-x-1 transition-transform" />
                   </button>
                   <button className="w-14 h-14 rounded-full border-2 border-[#22446b] flex items-center justify-center text-[#22446b] hover:bg-[#22446b] hover:text-white transition-all duration-300 animate-pulse-custom">
                      <FiPlay className="ml-1 text-xl" />
                   </button>
                 </div>
              </div>
            </div>

            {/* Right Content (Image) */}
            <div className={`w-full lg:w-1/2 h-[50vh] lg:h-auto relative mt-8 lg:mt-0 transition-all duration-1000 delay-500 ${
                index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}>
               <div className="relative w-full h-full flex justify-center lg:justify-end items-center">
                  {/* Tech Circle Background Animation */}
                  <TechCircle />
                  
                  <img 
                    src={slide.image} 
                    alt="Hero Slide" 
                    className="relative z-10 w-auto h-auto max-h-[600px] object-contain drop-shadow-2xl animate-float grayscale-[20%]"
                  />
               </div>
            </div>

          </div>
        </div>
      ))}

      {/* Navigation Arrows (Custom) - Inside Hero Section */}
      <div className="absolute bottom-4 sm:bottom-6 lg:bottom-10 right-4 sm:right-6 lg:right-10 z-20 flex gap-2 sm:gap-3 lg:gap-4 pointer-events-auto">
        <button onClick={prevSlide} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white text-[#22446b] shadow-lg flex items-center justify-center hover:bg-[#22446b] hover:text-white transition-all duration-300 group">
          <svg className="w-5 h-5 sm:w-6 sm:h-6 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <button onClick={nextSlide} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white text-[#22446b] shadow-lg flex items-center justify-center hover:bg-[#22446b] hover:text-white transition-all duration-300 group">
           <svg className="w-5 h-5 sm:w-6 sm:h-6 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>

      {/* Contact Modal */}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      <style jsx>{`
        .font-rajdhani {
          font-family: 'Rajdhani', sans-serif;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-spin-slow {
          animation: spin 20s linear infinite;
        }
        .animate-spin-slower {
          animation: spin 30s linear infinite;
        }
        .animate-spin-reverse-slower {
          animation: spin-reverse 25s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-pulse-slow {
          animation: pulse 8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .animate-pulse-custom {
            animation: pulse-ring 2s cubic-bezier(0.24, 0, 0.38, 1) infinite;
        }
        @keyframes pulse-ring {
            0% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7); }
            70% { box-shadow: 0 0 0 10px rgba(0, 0, 0, 0); }
            100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
        }
      `}</style>
    </div>
  );
}